import { useCallback, useState } from 'react';

import { ProcessDataFromClipboardParams } from 'ag-grid-community';
import { cloneDeep } from 'lodash';

export function useGridCopyPasteHandler<T>(initialData: T[]) {
    const [internalData, setInternalData] = useState(cloneDeep(initialData));
    const [isPasting, setIsPasting] = useState(false);

    const onPasteStart = useCallback(() => {
        setIsPasting(true);
    }, []);

    const onPasteEnd = useCallback(() => {
        setIsPasting(false);
    }, []);

    // this callback allows us to modify and act on the incoming clipboard data
    // the value returned by the callback will be passed through to the default paste handler
    // by default, Ag Grid will discard extra rows
    // we can prevent this by detecting when extra rows have been pasted and manually appending them to the grid
    // before proceeding with the default behavior
    const processDataFromClipboard = useCallback((params: ProcessDataFromClipboardParams): string[][] | null => {
        // https://www.ag-grid.com/react-data-grid/clipboard/#pasting-new-rows-at-the-bottom-of-the-grid
        const data = [...params.data];
        const emptyLastRow = data[data.length - 1][0] === '' && data[data.length - 1].length === 1;
        if (emptyLastRow) {
            data.splice(data.length - 1, 1);
        }
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const lastIndex = params.api!.getDisplayedRowCount() - 1;
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const focusedCell = params.api!.getFocusedCell();
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const focusedIndex = focusedCell!.rowIndex;

        // add new rows to receive the extra data
        if (focusedIndex + data.length - 1 > lastIndex) {
            const resultLastIndex = focusedIndex + (data.length - 1);
            const numRowsToAdd = resultLastIndex - lastIndex;
            const rowsToAdd: any[] = [];
            for (let i = 0; i < numRowsToAdd; i++) {
                rowsToAdd.push({}); // just append empty rows, the regular paste will populate them
            }
            setInternalData((prevState) => [...prevState, ...rowsToAdd]);
        }

        return data;
    }, []);

    return {
        internalData,
        setInternalData,
        isPasting,
        onPasteStart,
        onPasteEnd,
        processDataFromClipboard
    };
}
